// Client/src/components/billing/TaxAndBusinessFields.tsx
// Business-purchase checkbox + conditional business-name/tax-ID inputs, shared by
// BillingPayment.tsx and CreateTeamDialog.tsx. Individual is the overwhelmingly common
// case, so this is a low-emphasis checkbox rather than an Individual/Business toggle —
// Stripe validates the tax ID itself (and stays the source of truth for reverse-charge
// treatment), this component only collects the type + value.
import { Label } from '@/components/ui/label';

// Every tax_id type Stripe supports (https://docs.stripe.com/api/tax_ids/object#tax_id_object-type).
// Stripe only validates the format/existence of whichever type is selected — offering the
// full list costs nothing and just means fewer businesses have to check out as an
// "individual" for lack of a matching option. Sorted by label for a scannable dropdown.
export const TAX_ID_TYPES: { value: string; label: string }[] = [
  { value: 'ad_nrt', label: 'Andorra NRT' },
  { value: 'ao_tin', label: 'Angola TIN' },
  { value: 'al_tin', label: 'Albania TIN' },
  { value: 'ar_cuit', label: 'Argentina CUIT' },
  { value: 'am_tin', label: 'Armenia TIN' },
  { value: 'au_abn', label: 'Australia ABN' },
  { value: 'au_arn', label: 'Australia ARN' },
  { value: 'aw_tin', label: 'Aruba TIN' },
  { value: 'az_tin', label: 'Azerbaijan TIN' },
  { value: 'bs_tin', label: 'Bahamas TIN' },
  { value: 'bh_vat', label: 'Bahrain VAT' },
  { value: 'bd_bin', label: 'Bangladesh BIN' },
  { value: 'bb_tin', label: 'Barbados TIN' },
  { value: 'by_tin', label: 'Belarus TIN' },
  { value: 'bj_ifu', label: 'Benin IFU' },
  { value: 'bo_tin', label: 'Bolivia TIN' },
  { value: 'ba_tin', label: 'Bosnia and Herzegovina TIN' },
  { value: 'br_cnpj', label: 'Brazil CNPJ' },
  { value: 'br_cpf', label: 'Brazil CPF' },
  { value: 'bg_uic', label: 'Bulgaria UIC' },
  { value: 'bf_ifu', label: 'Burkina Faso IFU' },
  { value: 'cm_niu', label: 'Cameroon NIU' },
  { value: 'ca_bn', label: 'Canada BN' },
  { value: 'ca_gst_hst', label: 'Canada GST/HST' },
  { value: 'ca_pst_bc', label: 'Canada PST (British Columbia)' },
  { value: 'ca_pst_mb', label: 'Canada PST (Manitoba)' },
  { value: 'ca_pst_sk', label: 'Canada PST (Saskatchewan)' },
  { value: 'ca_qst', label: 'Canada QST (Quebec)' },
  { value: 'cv_nif', label: 'Cabo Verde NIF' },
  { value: 'cl_tin', label: 'Chile TIN' },
  { value: 'cn_tin', label: 'China TIN' },
  { value: 'co_nit', label: 'Colombia NIT' },
  { value: 'cr_tin', label: 'Costa Rica TIN' },
  { value: 'hr_oib', label: 'Croatia OIB' },
  { value: 'do_rcn', label: 'Dominican Republic RCN' },
  { value: 'ec_ruc', label: 'Ecuador RUC' },
  { value: 'eg_tin', label: 'Egypt TIN' },
  { value: 'sv_nit', label: 'El Salvador NIT' },
  { value: 'et_tin', label: 'Ethiopia TIN' },
  { value: 'eu_vat', label: 'EU VAT Number' },
  { value: 'eu_oss_vat', label: 'EU One Stop Shop VAT' },
  { value: 'fo_vat', label: 'Faroe Islands VAT' },
  { value: 'ge_vat', label: 'Georgia VAT' },
  { value: 'de_stn', label: 'Germany Tax Number (Steuernummer)' },
  { value: 'gi_tin', label: 'Gibraltar TIN' },
  { value: 'gn_nif', label: 'Guinea NIF' },
  { value: 'hk_br', label: 'Hong Kong BR' },
  { value: 'hu_tin', label: 'Hungary TIN' },
  { value: 'is_vat', label: 'Iceland VAT' },
  { value: 'in_gst', label: 'India GST' },
  { value: 'id_npwp', label: 'Indonesia NPWP' },
  { value: 'il_vat', label: 'Israel VAT' },
  { value: 'it_cf', label: 'Italy Codice Fiscale' },
  { value: 'jp_cn', label: 'Japan Corporate Number' },
  { value: 'jp_rn', label: 'Japan Registered Number' },
  { value: 'jp_trn', label: 'Japan Tax Registration Number' },
  { value: 'ke_pin', label: 'Kenya PIN' },
  { value: 'kz_bin', label: 'Kazakhstan BIN' },
  { value: 'kg_tin', label: 'Kyrgyzstan TIN' },
  { value: 'kh_tin', label: 'Cambodia TIN' },
  { value: 'kr_brn', label: 'South Korea BRN' },
  { value: 'la_tin', label: 'Laos TIN' },
  { value: 'li_uid', label: 'Liechtenstein UID' },
  { value: 'li_vat', label: 'Liechtenstein VAT' },
  { value: 'lk_vat', label: 'Sri Lanka VAT' },
  { value: 'ma_vat', label: 'Morocco VAT' },
  { value: 'md_vat', label: 'Moldova VAT' },
  { value: 'me_pib', label: 'Montenegro PIB' },
  { value: 'mk_vat', label: 'North Macedonia VAT' },
  { value: 'mr_nif', label: 'Mauritania NIF' },
  { value: 'mx_rfc', label: 'Mexico RFC' },
  { value: 'my_frp', label: 'Malaysia FRP' },
  { value: 'my_itn', label: 'Malaysia ITN' },
  { value: 'my_sst', label: 'Malaysia SST' },
  { value: 'ng_tin', label: 'Nigeria TIN' },
  { value: 'no_vat', label: 'Norway VAT' },
  { value: 'no_voec', label: 'Norway VOEC' },
  { value: 'np_pan', label: 'Nepal PAN' },
  { value: 'nz_gst', label: 'New Zealand GST' },
  { value: 'om_vat', label: 'Oman VAT' },
  { value: 'pe_ruc', label: 'Peru RUC' },
  { value: 'ph_tin', label: 'Philippines TIN' },
  { value: 'pl_nip', label: 'Poland NIP' },
  { value: 'py_ruc', label: 'Paraguay RUC' },
  { value: 'ro_tin', label: 'Romania TIN' },
  { value: 'rs_pib', label: 'Serbia PIB' },
  { value: 'ru_inn', label: 'Russia INN' },
  { value: 'ru_kpp', label: 'Russia KPP' },
  { value: 'sa_vat', label: 'Saudi Arabia VAT' },
  { value: 'sn_ninea', label: 'Senegal NINEA' },
  { value: 'sg_gst', label: 'Singapore GST' },
  { value: 'sg_uen', label: 'Singapore UEN' },
  { value: 'si_tin', label: 'Slovenia TIN' },
  { value: 'za_vat', label: 'South Africa VAT' },
  { value: 'es_cif', label: 'Spain CIF' },
  { value: 'sr_fin', label: 'Suriname FIN' },
  { value: 'ch_vat', label: 'Switzerland VAT' },
  { value: 'ch_uid', label: 'Switzerland UID' },
  { value: 'tw_vat', label: 'Taiwan VAT' },
  { value: 'tj_tin', label: 'Tajikistan TIN' },
  { value: 'tz_vat', label: 'Tanzania VAT' },
  { value: 'th_vat', label: 'Thailand VAT' },
  { value: 'tr_tin', label: 'Turkey TIN' },
  { value: 'ua_vat', label: 'Ukraine VAT' },
  { value: 'ae_trn', label: 'UAE TRN' },
  { value: 'gb_vat', label: 'UK VAT Number' },
  { value: 'ug_tin', label: 'Uganda TIN' },
  { value: 'us_ein', label: 'US EIN' },
  { value: 'uy_ruc', label: 'Uruguay RUC' },
  { value: 'uz_tin', label: 'Uzbekistan TIN' },
  { value: 'uz_vat', label: 'Uzbekistan VAT' },
  { value: 've_rif', label: 'Venezuela RIF' },
  { value: 'vn_tin', label: 'Vietnam TIN' },
  { value: 'cd_nif', label: 'DR Congo NIF' },
  { value: 'zm_tin', label: 'Zambia TIN' },
  { value: 'zw_tin', label: 'Zimbabwe TIN' },
].sort((a, b) => a.label.localeCompare(b.label));

export type CustomerType = 'individual' | 'business';

interface TaxAndBusinessFieldsProps {
  customerType: CustomerType;
  onCustomerTypeChange: (value: CustomerType) => void;
  businessName: string;
  onBusinessNameChange: (value: string) => void;
  taxIdType: string;
  onTaxIdTypeChange: (value: string) => void;
  taxIdValue: string;
  onTaxIdValueChange: (value: string) => void;
  disabled?: boolean;
}

export const TaxAndBusinessFields = ({
  customerType,
  onCustomerTypeChange,
  businessName,
  onBusinessNameChange,
  taxIdType,
  onTaxIdTypeChange,
  taxIdValue,
  onTaxIdValueChange,
  disabled,
}: TaxAndBusinessFieldsProps) => {
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 cursor-pointer w-fit">
        <input
          type="checkbox"
          checked={customerType === 'business'}
          onChange={(e) => onCustomerTypeChange(e.target.checked ? 'business' : 'individual')}
          disabled={disabled}
          className="h-4 w-4 text-indigo-600 rounded border-gray-300 dark:border-white/20 focus:ring-indigo-400"
        />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">I'm purchasing as a business</span>
      </label>

      {customerType === 'business' && (
        <div className="space-y-3 bg-gray-50/50 dark:bg-white/5 p-4 rounded-xl border border-gray-200 dark:border-white/10">
          <div>
            <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Business Name</Label>
            <input
              type="text"
              placeholder="Acme Ltd"
              value={businessName}
              onChange={(e) => onBusinessNameChange(e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2.5 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent shadow-sm text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tax ID Type</Label>
              <select
                value={taxIdType}
                onChange={(e) => onTaxIdTypeChange(e.target.value)}
                disabled={disabled}
                className="w-full px-3 py-2.5 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent shadow-sm text-sm"
              >
                <option value="" disabled>Select type</option>
                {TAX_ID_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tax ID</Label>
              <input
                type="text"
                placeholder="IE1234567T"
                value={taxIdValue}
                onChange={(e) => onTaxIdValueChange(e.target.value)}
                disabled={disabled}
                className="w-full px-3 py-2.5 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent shadow-sm text-sm"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Valid cross-border business tax IDs may qualify for reverse-charge (zero VAT) treatment.
          </p>
        </div>
      )}
    </div>
  );
};
